import { Injectable } from '@angular/core';
import {
  MessagePosition,
  MessageType,
  SnackBarService,
} from '@epicuro-next/ataraxia/snack-bar';
import { UsersApiService } from '@epicuro-next/data/users';
import {
  APP_AUTH_LEVEL_PATH,
  APP_TOP_LEVEL_PATH,
} from '@epicuro-next/navigation/constants';
import { go } from '@epicuro-next/state/router-store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import {
  changePassword,
  changePasswordFail,
  changePasswordSuccess,
  resetPassword,
  resetPasswordFailed,
} from '../actions';
import { HttpErrorResponse } from '@angular/common/http';

interface ChangePasswordError {
  message: string;
  modelState: {
    [key: string]: string[];
  };
}

@Injectable()
export class UserPasswordEffects {
  constructor(
    private actions$: Actions,
    private userService: UsersApiService,
    private snackBarService: SnackBarService,
  ) {}

  public changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changePassword),
      switchMap((action) =>
        this.userService.changePassword(action.passwordChangeRequest).pipe(
          map(() => changePasswordSuccess()),
          catchError((err) => {
            const error: ChangePasswordError = err.error;
            let errors: string[] = [];
            try {
              errors = Object.entries(error.modelState)
                .map(([_, value]) => value)
                .reduce((prev, curr) => [...prev, ...curr], [] as string[]);
            } catch {
              // Catch edge cases where/if BE doesn't send errors
              errors = [
                $localize`:@@userMenuChangePasswordFailOldPasswordIncorrect:Provided old password doesn't match.`,
              ];
            }

            return of(changePasswordFail({ errors }));
          }),
        ),
      ),
    ),
  );

  public changePasswordSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(changePasswordSuccess),
        tap(() =>
          this.snackBarService.show(
            $localize`:UserState@@changePasswordSuccessMessage:Your password has been changed`,
            MessagePosition.BOTTOM,
            MessageType.SUCCESS,
          ),
        ),
      ),
    { dispatch: false },
  );

  public changePasswordFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(changePasswordFail),
        tap(() =>
          this.snackBarService.show(
            $localize`:UserState@@changePasswordErrorMessage:There was an error while changing your password. Please try again later.`,
            MessagePosition.BOTTOM,
            MessageType.DANGER,
          ),
        ),
      ),
    { dispatch: false },
  );

  public resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resetPassword),
      switchMap(({ payload }) => {
        return this.userService.changePassword(payload).pipe(
          map(() => {
            return go({
              commands: [
                '/',
                APP_TOP_LEVEL_PATH.authentication.path,
                APP_AUTH_LEVEL_PATH.login.path,
              ],
            });
          }),
          catchError((e: HttpErrorResponse) => {
            const error = e.error as {
              modelState: { [key: string]: string[] };
            };

            const errors = [];
            for (var i in error.modelState) {
              for (var j in error.modelState[i]) {
                errors.push(error.modelState[i][j]);
              }
            }

            return of(resetPasswordFailed({ errors }));
          }),
        );
      }),
    ),
  );
}
